import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { parseCurrencyNumber } from '../../../../Utils/formatCurrency';
import { convertToVwMob } from '../../../../Utils/responsive';

import logoImg from '../../../../assets/Logo/main_logo.png';
import kakaoIconImg from '../../../../assets/Icon/project/kakao_icon.png';
import loadingLottie from '../../../../assets/Icon/lottie/Insider-loading.lottie';
import navbarExampleImg from '../../../../assets/Icon/project/navbar/navbar_mobile_example.png';
import profileDefaultImg from '../../../../assets/Icon/project/default-profile.png';
import emotionImg from '../../../../assets/img/emotion_mob.png';
import shareHandImg from '../../../../assets/img/share_hand.png';
import keymanImg from '../../../../assets/img/share_modal/keyman_img.png';

interface PreviewGeneralMobProps {
  title: string;
  projectId: string;
  goalAmount: string;
  currency: 'KRW' | 'USD';
  mediaPreviewUrl: string;
  videoPreviewSrc?: string; // Add this
  storyHtml: string;
  userFullName: string;
  onClose: () => void;
  onCreate: () => void;
  creating: boolean;
  creatingLabel: string;
}

const DonateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(20)} height={convertToVwMob(20)} viewBox="0 0 32 32" fill="none">
    <path
      d="M23.9993 28C23.9993 25.1711 22.8755 22.458 20.8752 20.4576C18.8748 18.4572 16.1617 17.3334 13.3327 17.3334C10.5037 17.3334 7.7906 18.4572 5.79021 20.4576C3.78982 22.458 2.66602 25.1711 2.66602 28"
      stroke="white"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.3327 17.3333C17.0146 17.3333 19.9993 14.3486 19.9993 10.6667C19.9993 6.98477 17.0146 4 13.3327 4C9.65078 4 6.66602 6.98477 6.66602 10.6667C6.66602 14.3486 9.65078 17.3333 13.3327 17.3333Z"
      stroke="white"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M29.3337 26.6667C29.3337 22.1733 26.6671 18 24.0004 16C24.8769 15.3424 25.5779 14.4788 26.0411 13.4857C26.5044 12.4926 26.7157 11.4006 26.6564 10.3064C26.5971 9.21219 26.2689 8.14946 25.701 7.21228C25.1331 6.2751 24.3429 5.49237 23.4004 4.93335"
      stroke="white"
      strokeWidth="2.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EnvelopeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(14)} height={convertToVwMob(14)} viewBox="0 0 14 14" fill="none">
    <path d="M11.666 2.33337H2.33268C1.68835 2.33337 1.16602 2.85571 1.16602 3.50004V10.5C1.16602 11.1444 1.68835 11.6667 2.33268 11.6667H11.666C12.3103 11.6667 12.8327 11.1444 12.8327 10.5V3.50004C12.8327 2.85571 12.3103 2.33337 11.666 2.33337Z" stroke="#5EBDE9" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12.8327 4.08337L7.60018 7.40837C7.42009 7.52121 7.21187 7.58105 6.99935 7.58105C6.78683 7.58105 6.57861 7.52121 6.39852 7.40837L1.16602 4.08337" stroke="#5EBDE9" strokeWidth="1.16667" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PreviewGeneralMob: React.FC<PreviewGeneralMobProps> = ({
  title,
  projectId,
  goalAmount,
  currency,
  mediaPreviewUrl,
  storyHtml,
  userFullName,
  onClose,
  onCreate,
  creating,
  creatingLabel,
  videoPreviewSrc,
}) => {
  const { t } = useTranslation('translation');
  // const numericGoal = useMemo(() => parseCurrencyNumber(goalAmount), [goalAmount]);
  const projectTitle = title || 'Project Title';
  
  const today = new Date();
  const dateString = `Created ${today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} (KR)`;

  return (
    <PreviewDialog onPointerDown={(e) => e.stopPropagation()}>
      <PreviewTopBar>
        <PreviewTopTitle>{t('page.newProject.preview.topTitle')}</PreviewTopTitle>
        <PreviewCloseBtn type="button" onClick={onClose}>
          <svg xmlns="http://www.w3.org/2000/svg" width={convertToVwMob(20)} height={convertToVwMob(20)} viewBox="0 0 20 20" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M19.3072 1.70719C19.4947 1.51965 19.6001 1.26528 19.6001 1.00005C19.6001 0.734821 19.4947 0.480454 19.3072 0.292908C19.1196 0.105362 18.8653 0 18.6 0C18.3348 0 18.0805 0.105362 17.8929 0.292908L9.80005 8.38577L1.70719 0.292908C1.51965 0.105362 1.26528 1.93113e-08 1.00005 2.12874e-08C0.734821 2.32635e-08 0.480454 0.105362 0.292908 0.292908C0.105362 0.480454 2.32635e-08 0.734821 2.12874e-08 1.00005C1.93113e-08 1.26528 0.105362 1.51965 0.292908 1.70719L8.38577 9.80005L0.292908 17.8929C0.105362 18.0805 0 18.3348 0 18.6C0 18.8653 0.105362 19.1196 0.292908 19.3072C0.480454 19.4947 0.734821 19.6001 1.00005 19.6001C1.26528 19.6001 1.51965 19.4947 1.70719 19.3072L9.80005 11.2143L17.8929 19.3072C18.0805 19.4947 18.3348 19.6001 18.6 19.6001C18.8653 19.6001 19.1196 19.4947 19.3072 19.3072C19.4947 19.1196 19.6001 18.8653 19.6001 18.6C19.6001 18.3348 19.4947 18.0805 19.3072 17.8929L11.2143 9.80005L19.3072 1.70719Z"
              fill="#A6A6A6"
            />
          </svg>
        </PreviewCloseBtn>
      </PreviewTopBar>

      <PreviewNavbar src={navbarExampleImg} alt="Navbar" />
      <PreviewBody>
        <GeneralContainer>
            <TopWhiteSection>
                {/* Header / Title Area */}
                <HeaderWrapper>
                    <HeaderArea>
                        <Title>{projectTitle}</Title>
                        <UserProfile>
                            <UserAvatar src={profileDefaultImg} />
                            <UserInfo>
                                <UserName>{userFullName || 'User Name'}</UserName>
                                <UserDateRow>
                                    <EnvelopeIcon />
                                    <UserDate>{dateString}</UserDate>
                                </UserDateRow>
                            </UserInfo>
                        </UserProfile>
                    </HeaderArea>
                </HeaderWrapper>

                {/* Main Image */}
                <MainImageArea>
                    {mediaPreviewUrl ? (
                        <>
                            <MainImage src={mediaPreviewUrl} />
                            <PreviewShareHand src={shareHandImg} alt="" />
                        </>
                    ) : videoPreviewSrc ? (
                        <PreviewIframe
                            src={videoPreviewSrc}
                            title="video preview"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <EmptyImage>{t('page.newProject.preview.emptyImage')}</EmptyImage>
                    )}
                </MainImageArea>
                
                {/* Donation Box */}
                <DonationBox>
                    <AmountArea>
                        <CurrentLabel>{t('page.newProject.preview.currentLabel')}</CurrentLabel>
                        <AmountValue>
                            <CurrencySymbol>{currency === 'KRW' ? '₩' : '$'}</CurrencySymbol>
                            0
                            <PerMonth>{t('page.newProject.preview.perMonth')}</PerMonth>
                        </AmountValue>
                    </AmountArea>

                    <ProgressBarTrack>
                        <ProgressBarFill style={{ width: '0%' }} />
                    </ProgressBarTrack>
                    <ProgressStats>
                        <StatText>0%</StatText>
                    </ProgressStats>
                    
                    <RaisedText>
                        Raised by 0 people in 0 days ($1 = ₩1,000)
                    </RaisedText>

                    <ActionButtons>
                        <DonateButton>
                            <BtnIcon><DonateIcon /></BtnIcon> {t('common.actions.donate')}
                        </DonateButton>
                        <ShareButton>
                            <BtnIcon><img src={kakaoIconImg} alt="kakao" width={convertToVwMob(20)} height={convertToVwMob(20)} /></BtnIcon> {t('common.actions.share')}
                        </ShareButton>
                    </ActionButtons>
                    
                    <ProjectLink>
                        go.missionfund.org/{projectId || 'sample'}
                    </ProjectLink>
                    
                    <KeymanText>
                        <KeymanImage src={keymanImg} alt={t('page.newProject.preview.keymanBadge')} />
                    </KeymanText>
                </DonationBox>
            </TopWhiteSection>

            {/* Bottom Content */}
            <BottomSection>
                <StorySection>
                    <SectionLabel>TOTAL SHARES</SectionLabel>
                    <StoryCard>
                        <StoryContent>
                             {storyHtml ? (
                                <div dangerouslySetInnerHTML={{ __html: storyHtml }} />
                              ) : (
                                <EmptyStory>{t('page.newProject.preview.emptyStory')}</EmptyStory>
                              )}
                        </StoryContent>
                         <ReactionImage src={emotionImg} />
                    </StoryCard>
                </StorySection>

                <FirstSponsorBox>
                    <FirstSponsorTitle>{t('page.newProject.preview.firstSponsorTitle')}</FirstSponsorTitle>
                    <EmptyWhiteBox />
                </FirstSponsorBox>
            </BottomSection>
        </GeneralContainer>

        <PreviewFooterArea>
          <FooterLogo src={logoImg} alt="Mission Fund" />
          <FooterMission>
            <FooterMissionText>{t('page.newProject.preview.footerMissionKo')}</FooterMissionText>
            <FooterMissionText $blue>MissionFund helps the Body of Christ fulfill its mission.</FooterMissionText>
          </FooterMission>
             <FooterButtons>
            <FooterButton>{t('page.newProject.preview.footerButtons.receipt')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.donations')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.footerButtons.story')}</FooterButton>
            <FooterButton>{t('page.newProject.preview.faq')}</FooterButton>
          </FooterButtons>

          <FooterDesc>
            <p>{t('page.newProject.preview.footerInfo.line1')}</p>
            <p>{t('page.newProject.preview.footerInfo.line2')}</p>
            <p>{t('page.newProject.preview.footerInfo.line3')}</p>
          </FooterDesc>

          <FooterContact>
            {t('page.newProject.preview.footerContact.line1')}<br />
            {t('page.newProject.preview.footerContact.line2')}<br />
            {t('page.newProject.preview.footerContact.line3')}
          </FooterContact>
        </PreviewFooterArea>
      </PreviewBody>

      <PreviewFooter>
        <PreviewConfirm
          type="button"
          onClick={onCreate}
          disabled={creating}
          $loading={creating}
          aria-busy={creating}
          aria-label={creating ? creatingLabel : undefined}
        >
          {creating ? (
            <LoadingWrapper>
              <DotLottieReact
                src={loadingLottie}
                autoplay
                loop
                style={{ width: '100%', height: '100%' }}
              />
            </LoadingWrapper>
          ) : t('common.actions.confirm')}
        </PreviewConfirm>
      </PreviewFooter>
    </PreviewDialog>
  );
};

export default PreviewGeneralMob;

// Styled Components

const PreviewDialog = styled.div`
  position: fixed;
  inset: 0;
  height: 100dvh;
  z-index: 1100;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PreviewTopBar = styled.div`
  height: ${convertToVwMob(56)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  position: relative;
  flex-shrink: 0;
  padding: 0 ${convertToVwMob(16)};
  background: #fff;
`;

const PreviewTopTitle = styled.div`
  font-family: Pretendard;
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  color: #000;
  line-height: 150%;
`;

const PreviewCloseBtn = styled.button`
  margin-left: auto;
  height: ${convertToVwMob(40)};
  width: ${convertToVwMob(40)};
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const PreviewNavbar = styled.img`
  width: 100%;
  height: auto;
  display: block;
  flex-shrink: 0;
`;

const PreviewBody = styled.div`
  flex: 1;
  min-height: 0;
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding-bottom: ${convertToVwMob(20)};
`;

const GeneralContainer = styled.div`
  width: 100%;
  padding: ${convertToVwMob(16)};
`;

const HeaderArea = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${convertToVwMob(12)};
    margin-bottom: ${convertToVwMob(24)};
    min-width: 0;
`;

const Title = styled.h1`
    color: #171717;
    font-family: "Noto Sans KR";
    font-size: ${convertToVwMob(20)};
    font-style: normal;
    font-weight: 600;
    line-height: 150%;
    margin: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const UserProfile = styled.div`
    display: flex;
    align-items: center;
    gap: ${convertToVwMob(10)};
    min-width: 0;
`;

const UserAvatar = styled.img`
    width: ${convertToVwMob(40)};
    height: ${convertToVwMob(40)};
    border-radius: 50%;
    background: #ccc;
    flex-shrink: 0;
`;

const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    flex: 1;
`;

const UserName = styled.div`
    color: #6B7280;
    font-family: "Noto Sans KR";
    font-size: ${convertToVwMob(14)};
    font-style: normal;
    font-weight: 350;
    line-height: ${convertToVwMob(21)};
    max-width: 100%;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const UserDateRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${convertToVwMob(4)};
    min-width: 0;
    max-width: 100%;
`;

const UserDate = styled.div`
    color: #5EBDE9;
    font-family: "Noto Sans KR";
    font-size: ${convertToVwMob(13)};
    font-style: normal;
    font-weight: 350;
    line-height: ${convertToVwMob(19.5)};
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
`;

const TopWhiteSection = styled.div`
    background: #fff;
    border-top-left-radius: ${convertToVwMob(8)};
    border-top-right-radius: ${convertToVwMob(8)};
    overflow: hidden;
`;

const HeaderWrapper = styled.div`
    padding: ${convertToVwMob(24)} ${convertToVwMob(24)} 0;
`;

const MainImageArea = styled.div`
    width: 100%;
    aspect-ratio: 4/3;
    background: #eee;
    overflow: hidden;
    position: relative;
`;

const PreviewShareHand = styled.img`
  position: absolute;
  bottom: 0;
  right: ${convertToVwMob(20)};
  width: ${convertToVwMob(80)};
  height: auto;
  z-index: 1;
`;

const MainImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const PreviewIframe = styled.iframe`
    width: 100%;
    height: 100%;
    border: none;
`;

const EmptyImage = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #aaa;
    font-size: ${convertToVwMob(14)};
`;

const DonationBox = styled.div`
    background: #F1F8E9;
    padding: ${convertToVwMob(24)};
    display: flex;
    flex-direction: column;
`;

const AmountArea = styled.div`
    margin-bottom: ${convertToVwMob(12)};
`;

const CurrentLabel = styled.div`
    font-size: ${convertToVwMob(14)};
    color: #666;
`;

const AmountValue = styled.div`
    font-size: ${convertToVwMob(32)};
    font-weight: 700;
    color: #888;
    display: flex;
    align-items: baseline;
    gap: ${convertToVwMob(4)};
`;

const CurrencySymbol = styled.span`
    font-size: ${convertToVwMob(24)};
    color: #aaa;
`;

const PerMonth = styled.span`
    font-size: ${convertToVwMob(16)};
    color: #aaa;
`;

const ProgressBarTrack = styled.div`
    height: ${convertToVwMob(8)};
    background: #fff;
    border-radius: ${convertToVwMob(4)};
    margin-bottom: ${convertToVwMob(4)};
`;

const ProgressBarFill = styled.div`
    height: 100%;
    background: #88C541;
    border-radius: ${convertToVwMob(4)};
`;

const ProgressStats = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: ${convertToVwMob(8)};
`;

const StatText = styled.span`
    font-size: ${convertToVwMob(14)};
    font-weight: 700;
    color: #666;
`;

const RaisedText = styled.div`
    font-size: ${convertToVwMob(12)};
    color: #888;
    margin-bottom: ${convertToVwMob(20)};
`;

const ActionButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${convertToVwMob(10)};
    margin-bottom: ${convertToVwMob(20)};
`;

const DonateButton = styled.button`
    width: 100%;
    height: ${convertToVwMob(48)};
    background: #D32F2F;
    color: #fff;
    border: none;
    border-radius: ${convertToVwMob(4)};
    font-size: ${convertToVwMob(18)};
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${convertToVwMob(8)};
`;

const ShareButton = styled.button`
    width: 100%;
    height: ${convertToVwMob(48)};
    background: #FDD835;
    color: #333;
    border: none;
    border-radius: ${convertToVwMob(4)};
    font-size: ${convertToVwMob(18)};
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${convertToVwMob(8)};
`;

const BtnIcon = styled.span`
    font-size: ${convertToVwMob(20)};
    display: flex;
`;

const ProjectLink = styled.div`
    font-size: ${convertToVwMob(14)};
    color: #666;
    text-align: center;
    margin-bottom: ${convertToVwMob(16)};
    word-break: break-all;
`;

const KeymanText = styled.div`
    display: flex;
    justify-content: center;
    margin-top: -${convertToVwMob(10)};
`;

const KeymanImage = styled.img`
    width: ${convertToVwMob(160)};
    height: auto;
    object-fit: contain;
`;

const BottomSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${convertToVwMob(30)};
    margin-top: ${convertToVwMob(30)};
`;

const StoryCard = styled.div`
    background: #fff;
    border-radius: ${convertToVwMob(12)};
    padding: ${convertToVwMob(24)};
`;

const StorySection = styled.div`
    display: flex;
    flex-direction: column;
`;

const SectionLabel = styled.div`
    color: #6B7280;
    font-family: "Noto Sans KR";
    font-size: ${convertToVwMob(21)};
    font-style: normal;
    font-weight: 350;
    line-height: ${convertToVwMob(20)};
    margin-bottom: ${convertToVwMob(16)};
    text-transform: uppercase;
`;

const StoryContent = styled.div`
    font-size: ${convertToVwMob(16)};
    line-height: 1.6;
    color: #444;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    word-break: break-word;
    margin-bottom: ${convertToVwMob(30)};

    p,
    div,
    span,
    li,
    ul,
    ol {
        overflow-wrap: anywhere;
        word-break: break-word;
    }
    
    img {
        max-width: 100%;
    }
`;

const EmptyStory = styled.div`
    color: #ccc;
    text-align: center;
    padding: ${convertToVwMob(20)};
`;

const ReactionImage = styled.img`
    width: 100%;
    height: auto;
    margin-top: ${convertToVwMob(20)};
`;

const FirstSponsorBox = styled.div`
    margin-top: ${convertToVwMob(20)};
`;

const FirstSponsorTitle = styled.div`
    font-size: ${convertToVwMob(14)};
    font-weight: 700;
    color: #89C4F4;
    margin-bottom: ${convertToVwMob(16)};
    line-height: ${convertToVwMob(20)};
`;

const EmptyWhiteBox = styled.div`
    background: #fff;
    border: 1px solid #eee;
    height: ${convertToVwMob(100)};
    border-radius: ${convertToVwMob(8)};
`;

const PreviewFooterArea = styled.div`
  width: 100%;
  margin-top: ${convertToVwMob(40)};
  background: #f0f1f2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${convertToVwMob(40)} ${convertToVwMob(24)} ${convertToVwMob(80)};
`;

const FooterLogo = styled.img`
  height: ${convertToVwMob(44)};
  margin-bottom: ${convertToVwMob(24)};
`;

const FooterMission = styled.div`
  text-align: center;
  margin-bottom: ${convertToVwMob(32)};
`;

const FooterMissionText = styled.div<{ $blue?: boolean }>`
  color: #27AAE2;
  text-align: center;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.5;
`;

const FooterButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${convertToVwMob(10)};
  margin-bottom: ${convertToVwMob(10)};
  width: 100%;
`;

const FooterButton = styled.div`
  background: #888;
  color: #fff;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(6)};
  font-family: Pretendard;
  font-size: ${convertToVwMob(14)};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
`;

const FooterDesc = styled.div`
  text-align: left;
  color: #374151;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(14)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.5;
  width: 100%;
  margin-top: ${convertToVwMob(32)};
  
  p {
    margin-bottom: ${convertToVwMob(12)};
    word-break: keep-all;
  }
`;

const FooterContact = styled.div`
  text-align: center;
  color: #6B7280;
  font-family: "Noto Sans KR";
  font-size: ${convertToVwMob(12)};
  font-style: normal;
  font-weight: 350;
  line-height: 1.4;
  width: 100%;
  border-top: 1px solid #aaa;
  padding-top: ${convertToVwMob(24)};
  margin-top: ${convertToVwMob(24)};
`;

const PreviewFooter = styled.div`
  position: relative;
  flex: 0 0 auto;
  z-index: 10;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  border-top: 1px solid #eeeeee;
  display: flex;
  justify-content: center;
  padding: ${convertToVwMob(12)} ${convertToVwMob(16)} calc(${convertToVwMob(24)} + env(safe-area-inset-bottom));
`;

const PreviewConfirm = styled.button<{ $loading?: boolean }>`
  width: 100%;
  height: ${convertToVwMob(64)};
  border-radius: ${({ $loading }) => ($loading ? convertToVwMob(10) : convertToVwMob(8))};
  border: none;
  background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#0b6773')};
  color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(18)};
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  white-space: nowrap;

  &:disabled {
    background: ${({ $loading }) => ($loading ? '#EBFCFF' : '#bcbcbc')};
    color: ${({ $loading }) => ($loading ? '#2BC2E1' : '#ffffff')};
    cursor: not-allowed;
  }
`;

const LoadingWrapper = styled.div`
  width: ${convertToVwMob(132)};
  height: ${convertToVwMob(132)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
